
import { ReactNode, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'
import { useIsAdmin } from '../hooks/isAdmin'
import { useIsCourseAdmin } from '../hooks/isCourseAdmin'
import { useIsCenterManager } from '../hooks/isCenterManager'
import { useIsContentReviewer } from '../hooks/isContentReviewer'
import { useIsTeacher } from '../hooks/isTeacher'
import { useHasMultipleOrganisations, useIsSupportingOrganisation, useCanManageOrphanedAccounts, useCanInviteCongregants } from '../hooks/organisations'
import { useCanAccessLeadership, useOrganisationName } from '../hooks/canAccessLeadership'
import { useHasGalleries } from '../hooks/galleries'
import { useHasPosts } from '../hooks/posts'
import { useCanManagePartnerships } from '../hooks/partnerships'
import {
  Container,
  Dropdown,
  Icon,
  Menu,
  Segment,
} from 'semantic-ui-react'

import { PATHS } from '../paths';

import './ResponsiveContainer.css'

import { MediaContextProvider, Media } from '../mobile'

// TODO: add an alerts menu item here
const SharedMenu = ({ variant }: { variant: 'desktop' | 'mobile' }) => {
  const isAdmin = useIsAdmin();
  const isCourseAdmin = useIsCourseAdmin();
  const isCenterManager = useIsCenterManager();
  const isContentReviewer = useIsContentReviewer();
  const hasMultipleOrganisations = useHasMultipleOrganisations();
  const isSupporter = useIsSupportingOrganisation();
  const canManageOrphans = useCanManageOrphanedAccounts();
  const canInvite = useCanInviteCongregants();
  const canAccessLeadership = useCanAccessLeadership();
  const hasGalleries = useHasGalleries();
  const hasPosts = useHasPosts();
  const canManagePartnerships = useCanManagePartnerships();
  const organisationName = useOrganisationName();
  const location = useLocation();

  // A supporter organisation runs no classes: no courses, no SKU tag group, no attendance.
  // Everything downstream of a class is therefore hidden there rather than leading to a page
  // that can only be empty. `isAdmin` is still required for each — this narrows, never widens.
  const runsClasses = !isSupporter;

  // The "Create" menu groups the create-new actions. Each item is role-gated;
  // the group itself shows if the viewer can create at least one thing.
  const canCreatePerson = isAdmin || isCenterManager;
  const canCreateClass = isAdmin && runsClasses;
  const showCreateMenu = canCreateClass || canCreatePerson || isAdmin;

  // The "Analysis" menu groups the four screens that read the data back rather than change it.
  // Each keeps the gate it already had — this regroups the bar, it does not widen anything.
  //
  // All four are `runsClasses`, so in a supporter organisation the whole menu disappears
  // rather than opening onto four pages that can only be empty.
  const canSeeReporting = isAdmin && runsClasses;
  const canSeeDashboard = isCourseAdmin && runsClasses;
  const canSeeAbsenceAlerts = (isCourseAdmin || isCenterManager) && runsClasses;
  const canSeeBlockStats = isAdmin && runsClasses;
  const showAnalysisMenu = canSeeReporting || canSeeDashboard || canSeeAbsenceAlerts || canSeeBlockStats;

  /**
   * The Partnerships menu is gated on the `MANAGE_PARTNERSHIPS` capability — the server's own
   * answer — rather than on `isAdmin`.
   *
   * **This narrows.** An administrator who is not also a partnership manager loses the menu,
   * which is the point: describing the work to the people funding it is a different authority
   * from administering the data, and bundling the two is what this separates. Anyone who
   * should keep it is given the role on `/admin/roles`; `scripts/grant-partnership-manager.ts`
   * seeds the first holder.
   *
   * `runsClasses` still applies: a supporter organisation has no courses to audit and is the
   * partner rather than the one describing itself.
   */
  const showPartnershipsMenu = canManagePartnerships && runsClasses;

  return (
    <>
      {/* Which organisation you are working in, before anything you could do in it. People now
          belong to more than one and switch between them, so a header that never said which one
          you were looking at made the two indistinguishable at a glance. */}
      {organisationName && (
        <Menu.Item header className='organisationName'>{organisationName}</Menu.Item>
      )}

      {runsClasses && (
        <Menu.Item as='a' href={PATHS.CLASSES} active={location.pathname === PATHS.CLASSES}>
          Classes
        </Menu.Item>
      )}
      <Menu.Item as='a' href={PATHS.PEOPLE} active={location.pathname === PATHS.PEOPLE}>
        People
      </Menu.Item>
      {/* Driven by the server's own answer, so whichever roles carry AUTHOR_LEADERSHIP_CONTENT
          in this organisation see it — including PARTNER_ADMIN, which a hardcoded role list
          would have missed. */}
      {canAccessLeadership && (
        <Menu.Item as='a' href={PATHS.LEADERSHIP} active={location.pathname === PATHS.LEADERSHIP}>
          Leadership
        </Menu.Item>
      )}
      {/* A supporter organisation's administrator inviting their own congregation. Gated on the
          server's answer rather than a role name, and on being a supporter organisation — a
          community invites people through its membership code and the people pages instead. */}
      {canInvite && isSupporter && (
        <Menu.Item as='a' href={PATHS.INVITES} active={location.pathname === PATHS.INVITES}>
          Invite people
        </Menu.Item>
      )}

      {showCreateMenu && (variant === 'desktop' ? (
        <Dropdown item trigger={<span><Icon name='plus' /> Create</span>} icon='dropdown'>
          <Dropdown.Menu>
            {canCreateClass && <Dropdown.Item as='a' href={PATHS.CLASS_CREATE}>Create class</Dropdown.Item>}
            {canCreatePerson && <Dropdown.Item as='a' href={PATHS.PEOPLE_CREATE}>Create person</Dropdown.Item>}
            {isAdmin && <Dropdown.Item as='a' href={PATHS.TAGS}>Create tag</Dropdown.Item>}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <>
          {canCreateClass && <Menu.Item as='a' href={PATHS.CLASS_CREATE} active={location.pathname === PATHS.CLASS_CREATE}>
            Create class
          </Menu.Item>}
          {canCreatePerson && <Menu.Item as='a' href={PATHS.PEOPLE_CREATE} active={location.pathname === PATHS.PEOPLE_CREATE}>
            Create person
          </Menu.Item>}
          {isAdmin && <Menu.Item as='a' href={PATHS.TAGS} active={location.pathname === PATHS.TAGS}>
            Create tag
          </Menu.Item>}
        </>
      ))}
      {/* Partnerships — the two screens that keep a supporter's picture of a community
          current. Both were top-level entries named after the page rather than the job:
          "Partnerships" and "Audit" say where you land, not what you are there to do.

          Collapsed on desktop, flat in the mobile side menu, as with Create, Content and
          Analysis. The bottom bar is left alone. */}
      {showPartnershipsMenu && (variant === 'desktop' ? (
        <Dropdown item trigger={<span><Icon name='handshake outline' /> Partnerships</span>} icon='dropdown'>
          <Dropdown.Menu>
            <Dropdown.Item as='a' href={PATHS.COURSE_DELIVERY_AUDIT}>Update audit info</Dropdown.Item>
            <Dropdown.Item as='a' href={PATHS.PARTNERSHIPS}>Update community info</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <>
          <Menu.Item as='a' href={PATHS.COURSE_DELIVERY_AUDIT} active={location.pathname === PATHS.COURSE_DELIVERY_AUDIT}>
            Update audit info
          </Menu.Item>
          <Menu.Item as='a' href={PATHS.PARTNERSHIPS} active={location.pathname === PATHS.PARTNERSHIPS}>
            Update community info
          </Menu.Item>
        </>
      ))}

      {/* Content — posting, reviewing, and the places content lives.
          Collapsed on desktop, flat in the mobile side menu.

          **Where Gallery and My posts sit depends on who is looking.** A reviewer has five
          other content entries, so those two join them in the dropdown rather than adding two
          more top-level items to an already crowded bar. Everybody else has no Content menu at
          all, so for them they are top-level — a dropdown holding one item is worse than the
          item.

          Each keeps its own gate, and neither is shown on the strength of a role:
            - Gallery    `hasGalleries` — a grant, not a role, and scoped to the organisation
                         being worked in
            - My posts   `hasPosts` — hidden until there is something to look at

          The mobile side menu lists everything flat: it is the overflow drawer, where a long
          list costs nothing, and collapsing it there is how these pages became unreachable on a
          phone in the first place. The bottom bar is deliberately left alone — it holds a
          handful of everyday destinations. */}
      {isContentReviewer && (variant === 'desktop' ? (
        <Dropdown item trigger={<span><Icon name='edit outline' /> Content</span>} icon='dropdown'>
          <Dropdown.Menu>
            <Dropdown.Item as='a' href={PATHS.POSTS_CREATE}>Create post</Dropdown.Item>
            <Dropdown.Item as='a' href={PATHS.POST_REVIEWS}>Review posts</Dropdown.Item>
            <Dropdown.Item as='a' href={PATHS.POSTS_SCHEDULED}>Scheduled posts</Dropdown.Item>
            <Dropdown.Item as='a' href={PATHS.POSTS_ENGAGEMENT}>Post engagement</Dropdown.Item>
            {/* Streams, their readers, supporter subscriptions and the reposting rules all
                live on one screen. It was routed but unreachable from the menu, so the only
                way in was to know the URL. */}
            <Dropdown.Item as='a' href={PATHS.ADMIN_STREAMS}>Streams &amp; reposting</Dropdown.Item>
            {hasGalleries && <Dropdown.Item as='a' href={PATHS.GALLERY}>Gallery</Dropdown.Item>}
            {hasPosts && <Dropdown.Item as='a' href={PATHS.MY_POSTS}>My posts</Dropdown.Item>}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <>
          <Menu.Item as='a' href={PATHS.POSTS_CREATE} active={location.pathname === PATHS.POSTS_CREATE}>
            Create post
          </Menu.Item>
          <Menu.Item as='a' href={PATHS.POST_REVIEWS} active={location.pathname === PATHS.POST_REVIEWS}>
            Review posts
          </Menu.Item>
          <Menu.Item as='a' href={PATHS.POSTS_SCHEDULED} active={location.pathname === PATHS.POSTS_SCHEDULED}>
            Scheduled posts
          </Menu.Item>
          <Menu.Item as='a' href={PATHS.POSTS_ENGAGEMENT} active={location.pathname === PATHS.POSTS_ENGAGEMENT}>
            Post engagement
          </Menu.Item>
          <Menu.Item as='a' href={PATHS.ADMIN_STREAMS} active={location.pathname === PATHS.ADMIN_STREAMS}>
            Streams &amp; reposting
          </Menu.Item>
        </>
      ))}

      {/* Top level for everyone who does not get them inside the Content menu — which is
          non-reviewers on desktop, and everybody in the mobile side menu, where nothing is
          collapsed. */}
      {hasGalleries && (!isContentReviewer || variant === 'mobile') && (
        <Menu.Item as='a' href={PATHS.GALLERY} active={location.pathname === PATHS.GALLERY}>
          <Icon name='images' /> Gallery
        </Menu.Item>
      )}
      {hasPosts && (!isContentReviewer || variant === 'mobile') && (
        <Menu.Item as='a' href={PATHS.MY_POSTS} active={location.pathname === PATHS.MY_POSTS}>
          <Icon name='edit outline' /> My posts
        </Menu.Item>
      )}

      {/* Analysis — the four screens that read the data back rather than changing it.
          They were four separate top-level entries, which is most of why the bar ran out of
          room on a laptop.

          **Flat on mobile, not hidden.** The Create menu does the same, and the Content menu
          does not: it is `variant === 'desktop'` outright, so on a phone its entries simply do
          not exist. Grouping a menu should not be how a page becomes unreachable. */}
      {showAnalysisMenu && (variant === 'desktop' ? (
        <Dropdown item trigger={<span><Icon name='chart bar' /> Analysis</span>} icon='dropdown'>
          <Dropdown.Menu>
            {canSeeReporting && <Dropdown.Item as='a' href={PATHS.ADMIN_TOOLS}>Reporting</Dropdown.Item>}
            {canSeeDashboard && <Dropdown.Item as='a' href={PATHS.DASHBOARD}>Dashboard</Dropdown.Item>}
            {canSeeAbsenceAlerts && <Dropdown.Item as='a' href={PATHS.ATTENDANCE}>Absence Alerts</Dropdown.Item>}
            {canSeeBlockStats && <Dropdown.Item as='a' href={PATHS.BLOCK_ATTENDANCE}>Block Stats</Dropdown.Item>}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <>
          {canSeeReporting && <Menu.Item as='a' href={PATHS.ADMIN_TOOLS} active={location.pathname === PATHS.ADMIN_TOOLS}>
            Reporting
          </Menu.Item>}
          {canSeeDashboard && <Menu.Item as='a' href={PATHS.DASHBOARD} active={location.pathname === PATHS.DASHBOARD}>
            Dashboard
          </Menu.Item>}
          {canSeeAbsenceAlerts && <Menu.Item as='a' href={PATHS.ATTENDANCE} active={location.pathname === PATHS.ATTENDANCE}>
            Absence Alerts
          </Menu.Item>}
          {canSeeBlockStats && <Menu.Item as='a' href={PATHS.BLOCK_ATTENDANCE} active={location.pathname === PATHS.BLOCK_ATTENDANCE}>
            Block Stats
          </Menu.Item>}
        </>
      ))}
      {/* <Menu.Item as='a'>Work</Menu.Item> */}

      {variant === 'desktop' ? (
        <Menu.Menu position='right'>
          <Dropdown item trigger={<Icon name='user' />} icon='dropdown'>
            <Dropdown.Menu>
              {/* Shown only to people who belong to more than one organisation — for everybody
                  else it would lead to a page with a single option. Settings carries the same
                  entry; this one is here because the user menu is where people look for it. */}
              {hasMultipleOrganisations && (
                <Dropdown.Item as='a' href={PATHS.ORGANISATIONS}>Switch organisation</Dropdown.Item>
              )}
              {/* One person holds the capability, so this is invisible to everybody else. */}
              {canManageOrphans && (
                <Dropdown.Item as='a' href={PATHS.ADMIN_ORPHANED_ACCOUNTS}>Orphaned accounts</Dropdown.Item>
              )}
              <Dropdown.Item as='a' href={PATHS.ADMIN_SETTINGS}>Settings</Dropdown.Item>
              <Dropdown.Item as='a' href={PATHS.LOGOUT}>Logout</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Menu.Menu>
      ) : (
        <>
          {hasMultipleOrganisations && (
            <Menu.Item as='a' href={PATHS.ORGANISATIONS} active={location.pathname === PATHS.ORGANISATIONS}>
              Switch organisation
            </Menu.Item>
          )}
          {canManageOrphans && (
            <Menu.Item as='a' href={PATHS.ADMIN_ORPHANED_ACCOUNTS} active={location.pathname === PATHS.ADMIN_ORPHANED_ACCOUNTS}>
              Orphaned accounts
            </Menu.Item>
          )}
          <Menu.Item as='a' href={PATHS.ADMIN_SETTINGS} active={location.pathname === PATHS.ADMIN_SETTINGS}>
            Settings
          </Menu.Item>
          <Menu.Item as='a' href={PATHS.LOGOUT}>Logout</Menu.Item>
        </>
      )}
  </>
  )
}

const DesktopContainer = ({ children }: { children: ReactNode }) => {
  const [fixed, setFixed] = useState(true);

  return (
    <Media greaterThan='mobile'>
      <InView
        threshold={1}
        onChange={() => setFixed(!fixed)}
      >
        <Segment
          inverted
          textAlign='center'
          vertical
        >
          <Menu
            fixed={fixed ? 'top' : undefined}
            inverted={!fixed}
            pointing={!fixed}
            secondary={!fixed}
            size='large'
          >
            <Container>
              <SharedMenu variant='desktop' />
            </Container>
          </Menu>
        </Segment>
      </InView>
      <br/>
      {children}
    </Media>
  )
}

const MobileContainer = ({ children }: {children: ReactNode}) => {
  const isTeacher = useIsTeacher();
  const [sidebarOpened, setSideBarOpened] = useState(false);

  if (isTeacher) {
    return (
      <Media at='mobile'> {children} </Media>
    )
  }

  return (
    <Media at='mobile'>
      <div className='mobile-pushable'>
        <div className={`mobile-drawer${sidebarOpened ? ' open' : ''}`}>
          <Menu inverted vertical fluid>
            <SharedMenu variant='mobile' />
          </Menu>
        </div>

        {sidebarOpened && (
          <div className='mobile-dimmer' onClick={() => setSideBarOpened(false)} />
        )}

        <div>
          <Segment
            inverted
            textAlign='center'
            vertical
            className='mobile-menu'
          >
            <Container>
              <Menu inverted pointing secondary size='large'>
                <Menu.Item position='right' onClick={() => setSideBarOpened(true)}>
                  <Icon name='sidebar' />
                </Menu.Item>
              </Menu>
            </Container>
          </Segment>

          {children}
        </div>
      </div>
    </Media>
  )
}

const ResponsiveContainer = ({ children }: {children: ReactNode}) => (
  <MediaContextProvider>
    <DesktopContainer>{children}</DesktopContainer>
    <MobileContainer>{children}</MobileContainer>
  </MediaContextProvider>
)

export { ResponsiveContainer }
